import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { customerCategoriesApi } from '@/api/customer-categories'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { showError } from '@/lib/error'
import {
  CUSTOMER_CATEGORY_TYPES,
  type CustomerCategory,
  type CustomerCategorySavePayload,
  type CustomerCategoryType,
} from '@/types/customer-category'
import { CustomerCategoryDialog } from './components/CustomerCategoryDialog'
import { CustomerCategoryTable } from './components/CustomerCategoryTable'

export function CustomerCategoriesPage() {
  const queryClient = useQueryClient()
  const [keyword, setKeyword] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [type, setType] = useState('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [editing, setEditing] = useState<CustomerCategory | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['customer-categories', searchTerm, type, page, pageSize],
    queryFn: () => customerCategoriesApi.list({
      keyword: searchTerm,
      type: type === 'all' ? '' : Number(type) as CustomerCategoryType,
      page,
      page_size: pageSize,
    }),
  })

  const saveMutation = useMutation({
    mutationFn: (payload: CustomerCategorySavePayload) => (
      editing ? customerCategoriesApi.update(editing.id, payload) : customerCategoriesApi.create(payload)
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-categories'] })
      setDialogOpen(false)
      setEditing(null)
      toast.success('客户类型已保存')
    },
    onError: (error) => showError(error, '保存失败'),
  })

  const deleteMutation = useMutation({
    mutationFn: (category: CustomerCategory) => customerCategoriesApi.delete(category.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-categories'] })
      toast.success('客户类型已删除')
    },
    onError: (error) => showError(error, '删除失败'),
  })

  const handleSearch = () => {
    setSearchTerm(keyword.trim())
    setPage(1)
  }

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize)
    setPage(1)
  }

  const handleOpenCreate = () => {
    setEditing(null)
    setDialogOpen(true)
  }

  const handleOpenEdit = (category: CustomerCategory) => {
    setEditing(category)
    setDialogOpen(true)
  }

  const handleDelete = (category: CustomerCategory) => {
    if (!window.confirm(`确定删除客户类型「${category.name}」吗？`)) return
    deleteMutation.mutate(category)
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">客户类型</h1>
          <p className="mt-1 text-sm text-muted-foreground">维护客户分类，用于客户筛选、标记和后续 VIP 配置</p>
        </div>
        <Button type="button" onClick={handleOpenCreate}>
          <Plus className="mr-2 h-4 w-4" />
          新建类型
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-background p-3">
        <div className="relative w-80 max-w-full">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSearch()
            }}
            placeholder="搜索类型名称"
            className="pl-9"
          />
        </div>
        <Button type="button" variant="outline" onClick={handleSearch}>
          搜索
        </Button>
        <Select value={type} onValueChange={(nextType) => {
          setType(nextType)
          setPage(1)
        }}>
          <SelectTrigger className="w-40" aria-label="筛选类型种类">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部类型</SelectItem>
            {CUSTOMER_CATEGORY_TYPES.map((item) => (
              <SelectItem key={item.value} value={String(item.value)}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="rounded-lg border py-12 text-center text-sm text-muted-foreground">加载中...</div>
      ) : (
        <CustomerCategoryTable
          categories={data?.list ?? []}
          onEdit={handleOpenEdit}
          onDelete={handleDelete}
        />
      )}

      <ListPagination
        page={page}
        pageSize={pageSize}
        total={data?.total || 0}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
      />

      <CustomerCategoryDialog
        open={dialogOpen}
        category={editing}
        isSaving={saveMutation.isPending}
        onOpenChange={(open) => {
          setDialogOpen(open)
          if (!open) setEditing(null)
        }}
        onSubmit={(payload) => saveMutation.mutate(payload)}
      />
    </div>
  )
}
